import type { TaskInputField } from '@/api'

/**
 * What the retry correction form holds per field: a checkbox's boolean, else the text typed, which a number field's
 * `v-model` hands back as a number once something numeric is in it.
 */
export type RetryValues = Record<string, string | number | boolean>

/**
 * The declared inputs a person can correct when retrying a run (#1132). A `file` input is left out: its stored
 * handle stands, and a retry form has no file to send again. Keys the task does not declare — lineage, execution
 * metadata, retry counters — are never offered, because they are the OS's, not the person's.
 */
export function retryFields(fields: TaskInputField[] | null | undefined): TaskInputField[] {
    return (fields ?? []).filter(field => field.type !== 'file')
}

/** The form's starting values: each field as the stored input has it, so a person corrects only what is wrong. */
export function retryValues(
    fields: TaskInputField[] | null | undefined,
    stored: Record<string, unknown> | null | undefined,
): RetryValues {
    const values: RetryValues = {}
    for (const field of retryFields(fields)) {
        const value = stored?.[field.key]
        if (field.type === 'boolean') {
            values[field.key] = typeof value === 'boolean' ? value : field.default === true
            continue
        }
        values[field.key] = value === undefined || value === null ? '' : String(value)
    }
    return values
}

/** The input a retry's refusal names (`request` attaches it), or null for a refusal about the run as a whole. */
export function refusedField(caught: unknown): string | null {
    const field = caught && typeof caught === 'object' ? (caught as { field?: unknown }).field : undefined
    return typeof field === 'string' ? field : null
}

/**
 * The correction a retry sends: the declared values the form holds, and only those. A field left blank is not
 * sent, so the server reads the stored value (or the field's default, or refuses a required one by name).
 */
export function retryCorrection(
    fields: TaskInputField[] | null | undefined,
    values: RetryValues,
): Record<string, unknown> {
    const correction: Record<string, unknown> = {}
    for (const field of retryFields(fields)) {
        const value = values[field.key]
        if (field.type === 'boolean') {
            correction[field.key] = value === true
            continue
        }
        // A number field's `v-model` casts what is typed to a number (#1337): that is a corrected value, not a blank.
        const raw = typeof value === 'string' || typeof value === 'number' ? String(value) : ''
        if (raw.trim() === '') {
            continue
        }
        correction[field.key] = field.type === 'number' ? Number(raw) : raw
    }
    return correction
}
